/**
 * Exclusive menu / panel screens — only one may be visible at a time.
 *
 * When opening any registered screen, call {@link claimExclusiveUi} with that
 * screen's id first. Add new panel/overlay screen ids to
 * {@link EXCLUSIVE_UI_SCREEN_IDS} (or {@link registerExclusiveUiScreen}) so they
 * participate automatically; do not hand-roll per-panel hide lists.
 */

export const EXCLUSIVE_UI_SCREEN_IDS = [
  "settingsScreen",
  "profileScreen",
  "newsScreen",
  "notificationsScreen",
  "wallScreen",
  "adminPanelScreen",
  "leaderboardScreen",
  "statsScreen",
  "achievementsScreen",
  "tasksScreen",
  "battlePassScreen",
  "shopScreen",
  "inventoryScreen",
  "creditsScreen",
  "authScreen",
  "modeScreen",
  "sandboxScreen",
  "sandboxBaseScreen",
  "sandboxCommunityScreen",
  "gameHubScreen",
  "practiceScreen",
  "challengesScreen",
  "tournamentsScreen",
  "marathonScreen",
  "campaignScreen",
  "worldMapScreen",
  "soccerScreen",
  "speedrunLevelScreen",
  "speedrunCompleteScreen",
  "penaltyLevelScreen",
  "penaltyCompleteScreen",
  "rouletteWarnScreen",
  "gameOverScreen",
] as const;

export type ExclusiveUiScreenId = (typeof EXCLUSIVE_UI_SCREEN_IDS)[number];

export type ExclusiveUiHooks = {
  /** Default hide (typically hideEl). */
  hideElement: (el: HTMLElement) => void;
  /** Optional per-screen close (cleanup without navigating to Home). */
  closeHandlers?: Partial<Record<string, () => void>>;
};

const runtimeIds = new Set<string>(EXCLUSIVE_UI_SCREEN_IDS);
let hooks: ExclusiveUiHooks | null = null;

/** Wire hide/close behavior from the app shell (call once after hideEl exists). */
export function configureExclusiveUi(next: ExclusiveUiHooks): void {
  hooks = next;
}

/** Register a dynamically created exclusive panel/overlay screen id. */
export function registerExclusiveUiScreen(id: string): void {
  const trimmed = String(id || "").trim();
  if (trimmed) runtimeIds.add(trimmed);
}

function allExclusiveIds(): string[] {
  return [...runtimeIds];
}

/**
 * Close every exclusive menu/panel except `keepId` (the one about to open).
 * Pass `null`/`undefined` to close all.
 */
export function claimExclusiveUi(keepId?: ExclusiveUiScreenId | string | null): void {
  const keep = keepId ? String(keepId) : null;
  for (const id of allExclusiveIds()) {
    if (keep && id === keep) continue;
    const el = document.getElementById(id);
    if (!el || el.classList.contains("hidden")) continue;
    const special = hooks?.closeHandlers?.[id];
    if (special) {
      special();
      continue;
    }
    if (hooks) hooks.hideElement(el);
    else el.classList.add("hidden");
  }
}
